import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  Delete,
  Info,
  RotateRight,
  Lock,
} from "@mui/icons-material";

import { API_URL } from "../../helpers/constants";
import { getToken } from "../../helpers/tokenStorage";

const fetchJson = async (path, opts = {}) => {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    ...(opts.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  // REACT_APP_API_URL already ends in "/api" (see helpers/constants.js and
  // the memory note). Tolerate paths written with the "/api/..." prefix
  // — they were consistent with the swagger docs — by stripping the
  // duplicate segment before appending; without this, every DaariSecrets
  // fetch hits "/api/api/v1/daari/secrets/..." and 500s.
  const normalizedPath = path.startsWith("/api/") ? path.substring(4) : path;
  const res = await fetch(`${API_URL}${normalizedPath}`, { ...opts, headers });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.message) msg = j.message;
    } catch (_) {
      /* keep status-only */
    }
    throw new Error(msg);
  }
  if (res.status === 204) return null;
  return res.json();
};

/**
 * Daari Secrets — per-project key/value store bindable to apps.
 *
 * Pinned as the first tab inside DaariView. Detail page deep-link is
 * `/daari/secrets/<k8sName>` (k8sName param read via useParams). Values
 * are NEVER shown back from the server — only keys + metadata + bindings.
 *
 * Backed by:
 *   GET    /api/v1/daari/secrets/                  (list)
 *   GET    /api/v1/daari/secrets/{k8sName}          (detail)
 *   POST   /api/v1/daari/secrets/                  (create)
 *   PUT    /api/v1/daari/secrets/{k8sName}/values   (rotate)
 *   DELETE /api/v1/daari/secrets/{k8sName}          (delete)
 *   GET    /api/v1/daari/secrets/{k8sName}/bindings (list bound apps)
 */
export default function DaariSecrets() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { k8sName: deepLinkName } = useParams();

  const [secrets, setSecrets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [bindings, setBindings] = useState([]);
  const [rotateOpen, setRotateOpen] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchJson("/api/v1/daari/secrets/");
      setSecrets(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Deep-link: open detail dialog when the URL ends in /daari/secrets/<k8sName>.
  useEffect(() => {
    if (!deepLinkName) return;
    let cancelled = false;
    (async () => {
      try {
        const [d, b] = await Promise.all([
          fetchJson(`/api/v1/daari/secrets/${deepLinkName}`),
          fetchJson(`/api/v1/daari/secrets/${deepLinkName}/bindings`),
        ]);
        if (cancelled) return;
        setDetail(d);
        setBindings(b || []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deepLinkName]);

  const closeDetail = () => {
    setDetail(null);
    setBindings([]);
    if (deepLinkName) navigate("/daari/secrets");
  };

  const openDetail = async (k8sName) => {
    navigate(`/daari/secrets/${k8sName}`);
  };

  const handleDelete = async (k8sName) => {
    if (!window.confirm(`Delete Daari Secret '${k8sName}'?`)) return;
    try {
      await fetchJson(`/api/v1/daari/secrets/${k8sName}`, { method: "DELETE" });
      closeDetail();
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const panelSx = {
    p: 2,
    borderRadius: 2,
    mb: 2,
    bgcolor: isDark ? "background.paper" : "#fff",
    border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body1" fontWeight={600}>
            Daari Secrets
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Project-scoped key/value secrets — bindable to apps via service-binding.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateOpen(true)}
          size="small"
        >
          New secret
        </Button>
      </Stack>

      {error && (
        <Paper sx={{ ...panelSx, borderColor: "error.main" }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Paper>
      )}

      <Paper sx={{ ...panelSx, p: 0, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>k8sName</TableCell>
                <TableCell>Keys</TableCell>
                <TableCell>Source</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="caption" color="text.secondary">
                      loading…
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : secrets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="caption" color="text.secondary">
                      No Daari Secrets in this project yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                secrets.map((s) => (
                  <TableRow
                    key={s.k8sName}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => openDetail(s.k8sName)}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={500}>
                          {s.displayName || s.name}
                        </Typography>
                        {s.certManagerManaged && (
                          <Tooltip title="Managed by cert-manager — read-only">
                            <Chip
                              icon={<Lock sx={{ fontSize: 12 }} />}
                              label="cert-mgr"
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: "0.65rem" }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {s.k8sName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {(s.keys || []).join(", ") || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={s.source || "manual"}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.65rem" }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Details">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openDetail(s.k8sName); }}>
                          <Info fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          load();
        }}
        onError={setError}
      />

      <DetailDialog
        secret={detail}
        bindings={bindings}
        onClose={closeDetail}
        onRotate={() => setRotateOpen(true)}
        onDelete={() => handleDelete(detail?.k8sName)}
      />

      <RotateDialog
        open={rotateOpen}
        secret={detail}
        onClose={() => setRotateOpen(false)}
        onRotated={async () => {
          setRotateOpen(false);
          if (detail?.k8sName) {
            try {
              const fresh = await fetchJson(`/api/v1/daari/secrets/${detail.k8sName}`);
              setDetail(fresh);
            } catch (_) { /* keep existing detail */ }
          }
          load();
        }}
        onError={setError}
      />
    </Box>
  );
}

// ----- sub-components -----

function CreateDialog({ open, onClose, onCreated, onError }) {
  const [displayName, setDisplayName] = useState("");
  const [pairs, setPairs] = useState([{ k: "", v: "" }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setDisplayName("");
      setPairs([{ k: "", v: "" }]);
    }
  }, [open]);

  const data = useMemo(() => {
    const out = {};
    for (const p of pairs) {
      if (p.k.trim()) out[p.k.trim()] = p.v;
    }
    return out;
  }, [pairs]);

  const submit = async () => {
    setBusy(true);
    try {
      await fetchJson("/api/v1/daari/secrets/", {
        method: "POST",
        body: JSON.stringify({ displayName, data }),
      });
      onCreated();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Daari Secret</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoFocus
            fullWidth
          />
          {pairs.map((p, idx) => (
            <Stack key={idx} direction="row" spacing={1}>
              <TextField
                label="key"
                value={p.k}
                onChange={(e) => {
                  const next = [...pairs];
                  next[idx] = { ...next[idx], k: e.target.value };
                  setPairs(next);
                }}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="value"
                value={p.v}
                onChange={(e) => {
                  const next = [...pairs];
                  next[idx] = { ...next[idx], v: e.target.value };
                  setPairs(next);
                }}
                size="small"
                type="password"
                sx={{ flex: 2 }}
              />
            </Stack>
          ))}
          <Button
            size="small"
            onClick={() => setPairs([...pairs, { k: "", v: "" }])}
          >
            + add key
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !displayName.trim() || Object.keys(data).length === 0}
        >
          {busy ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailDialog({ secret, bindings, onClose, onRotate, onDelete }) {
  if (!secret) return null;
  return (
    <Dialog open={!!secret} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          {secret.displayName || secret.name}
          {secret.certManagerManaged && (
            <Chip
              icon={<Lock sx={{ fontSize: 12 }} />}
              label="cert-manager"
              size="small"
              variant="outlined"
            />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Field label="k8sName" value={secret.k8sName} monospace />
          <Field label="namespace" value={secret.namespace} monospace />
          <Field label="source" value={secret.source || "manual"} />
          <Field label="keys" value={(secret.keys || []).join(", ") || "—"} />
          <Field label="last rotated" value={secret.lastRotatedAt || "—"} />
          <Field label="created" value={secret.createdAt || "—"} />
          <Box>
            <Typography variant="caption" color="text.secondary">
              bindings
            </Typography>
            {bindings.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Not bound to any app.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {bindings.map((b, i) => (
                  <Typography key={i} variant="body2">
                    <Box component="span" sx={{ color: "text.secondary", mr: 0.5 }}>
                      {b.kind}/
                    </Box>
                    {b.name}
                  </Typography>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        {!secret.certManagerManaged && (
          <>
            <Button startIcon={<RotateRight />} onClick={onRotate}>
              Rotate
            </Button>
            <Button startIcon={<Delete />} color="error" onClick={onDelete}>
              Delete
            </Button>
          </>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function Field({ label, value, monospace }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontFamily: monospace ? "monospace" : undefined }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function RotateDialog({ open, secret, onClose, onRotated, onError }) {
  const [pairs, setPairs] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && secret?.keys) {
      setPairs(secret.keys.map((k) => ({ k, v: "" })));
    }
  }, [open, secret]);

  const data = useMemo(() => {
    const out = {};
    for (const p of pairs) {
      if (p.k.trim() && p.v !== "") out[p.k.trim()] = p.v;
    }
    return out;
  }, [pairs]);

  const submit = async () => {
    setBusy(true);
    try {
      await fetchJson(`/api/v1/daari/secrets/${secret.k8sName}/values`, {
        method: "PUT",
        body: JSON.stringify({ data }),
      });
      onRotated();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!secret) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rotate {secret.displayName || secret.name}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          Keys you leave blank keep their existing value.
        </Typography>
        <Stack spacing={1.5}>
          {pairs.map((p, idx) => (
            <Stack key={idx} direction="row" spacing={1}>
              <TextField
                value={p.k}
                disabled
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="new value"
                value={p.v}
                onChange={(e) => {
                  const next = [...pairs];
                  next[idx] = { ...next[idx], v: e.target.value };
                  setPairs(next);
                }}
                size="small"
                type="password"
                sx={{ flex: 2 }}
              />
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || Object.keys(data).length === 0}
        >
          {busy ? "Rotating…" : "Rotate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
